import { useEffect, useRef } from 'react';
import {
  pushAttachmentContext,
  useSalesOrderAttachmentFlow,
} from 'components/attachments';
import { useHydrateOrderAttachments } from 'hooks/useHydrateOrderAttachments';
import type {
  AttachmentContextHandle,
  AttachmentFlow,
} from 'components/attachments';
import type { SalesOrderAttachmentMeta } from 'types/generatedSchemaTypes';

type OrderAttachment = Pick<
  SalesOrderAttachmentMeta,
  'uid' | 'fileName' | 'mimeType' | 'sizeBytes'
>;

type UseSalesOrderAttachmentScreenArgs = {
  orderUid: string | null | undefined;
  orderAttachments: OrderAttachment[] | null | undefined;
  enableSalesOrderAttachments: boolean;
  additionalReadOnly?: boolean;
  onAttachmentChanged?: (orderUid: string) => Promise<void> | void;
};

type UseSalesOrderAttachmentScreenReturn = {
  attachmentFlow: AttachmentFlow;
};

export const useSalesOrderAttachmentScreen = ({
  orderUid,
  orderAttachments,
  enableSalesOrderAttachments,
  additionalReadOnly = false,
  onAttachmentChanged,
}: UseSalesOrderAttachmentScreenArgs): UseSalesOrderAttachmentScreenReturn => {
  const attachmentFlow = useSalesOrderAttachmentFlow({ onAttachmentChanged });
  const readOnly = additionalReadOnly || !enableSalesOrderAttachments;

  const handleRef = useRef<AttachmentContextHandle | null>(null);
  // Initial-push values via refs so the push effect can depend on orderUid
  // alone — re-pushing on every flow/readOnly change would pop+push the
  // holder, breaking the stack ordering that contextStack maintains.
  const flowRef = useRef(attachmentFlow);
  flowRef.current = attachmentFlow;
  const readOnlyRef = useRef(readOnly);
  readOnlyRef.current = readOnly;

  useEffect(() => {
    if (!orderUid) return;
    handleRef.current = pushAttachmentContext({
      orderUid,
      flow: flowRef.current,
      readOnly: readOnlyRef.current,
    });
    return () => {
      handleRef.current?.release();
      handleRef.current = null;
    };
  }, [orderUid]);

  useEffect(() => {
    handleRef.current?.update({ flow: attachmentFlow, readOnly });
  }, [attachmentFlow, readOnly]);

  useHydrateOrderAttachments({
    orderUid,
    orderAttachments,
    mode: orderAttachments === undefined ? 'observe' : 'hydrate',
  });

  return { attachmentFlow };
};
